import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RISK_LEVELS } from '@immigration-helper/shared';

// Static educational content (would come from API in production)
const educationContent = [
  {
    category: 'lawful',
    articles: [
      {
        slug: 'us-citizenship',
        title: 'U.S. Citizenship',
        titleEs: 'Ciudadanía Estadounidense',
        description: 'Learn about the rights and responsibilities of U.S. citizens.',
        readingTime: 5,
      },
      {
        slug: 'green-card-holder',
        title: 'Lawful Permanent Resident (Green Card)',
        titleEs: 'Residente Permanente Legal (Tarjeta Verde)',
        description: 'Understand what it means to be a green card holder.',
        readingTime: 6,
      },
      {
        slug: 'valid-visa-status',
        title: 'Valid Visa Status',
        titleEs: 'Estado de Visa Válida',
        description: 'Overview of temporary visa categories and maintaining status.',
        readingTime: 7,
      },
    ],
  },
  {
    category: 'protected',
    articles: [
      {
        slug: 'daca-explained',
        title: 'DACA Explained',
        titleEs: 'DACA Explicado',
        description: 'Everything you need to know about Deferred Action for Childhood Arrivals.',
        readingTime: 8,
      },
      {
        slug: 'temporary-protected-status',
        title: 'Temporary Protected Status (TPS)',
        titleEs: 'Estatus de Protección Temporal (TPS)',
        description: 'Learn about TPS eligibility and the designated countries.',
        readingTime: 6,
      },
      {
        slug: 'asylum-basics',
        title: 'Asylum and Refugee Status',
        titleEs: 'Asilo y Estatus de Refugiado',
        description: 'Understanding the difference between asylum and refugee status.',
        readingTime: 7,
      },
    ],
  },
  {
    category: 'at-risk',
    articles: [
      {
        slug: 'visa-overstay',
        title: 'Understanding Visa Overstay',
        titleEs: 'Entendiendo la Estancia Excedida',
        description: 'What happens if you stay past your authorized period.',
        readingTime: 5,
      },
      {
        slug: 'unlawful-presence-bars',
        title: 'The 3 and 10 Year Bars',
        titleEs: 'Las Barras de 3 y 10 Años',
        description: 'Learn about the consequences of unlawful presence.',
        readingTime: 6,
      },
      {
        slug: 'options-for-undocumented',
        title: 'Options for Undocumented Individuals',
        titleEs: 'Opciones para Personas Indocumentadas',
        description: 'Potential pathways and protections that may be available.',
        readingTime: 8,
      },
    ],
  },
];

export default function Education() {
  const { t, i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';

  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'lawful':
        return {
          title: t('education.categories.lawful'),
          color: RISK_LEVELS.lawful.color,
          bgColor: 'bg-green-50',
          textColor: 'text-green-800',
        };
      case 'protected':
        return {
          title: t('education.categories.protected'),
          color: RISK_LEVELS.protected.color,
          bgColor: 'bg-amber-50',
          textColor: 'text-amber-800',
        };
      case 'at-risk':
        return {
          title: t('education.categories.atRisk'),
          color: RISK_LEVELS.at_risk.color,
          bgColor: 'bg-orange-50',
          textColor: 'text-orange-800',
        };
      default:
        return {
          title: category,
          color: '#6b7280',
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-800',
        };
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t('education.title')}
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {t('education.subtitle')}
        </p>
      </div>

      {/* Content Categories */}
      <div className="space-y-12">
        {educationContent.map((section) => {
          const categoryInfo = getCategoryInfo(section.category);

          return (
            <section key={section.category}>
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: categoryInfo.color }}
                />
                <h2 className="text-2xl font-semibold text-gray-900">
                  {categoryInfo.title}
                </h2>
              </div>

              {/* Articles Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/education/${article.slug}`}
                    className="card hover:shadow-md transition-all group"
                  >
                    <div className={`inline-block px-2 py-1 rounded text-xs font-medium mb-3 ${categoryInfo.bgColor} ${categoryInfo.textColor}`}>
                      {categoryInfo.title}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {isSpanish && article.titleEs ? article.titleEs : article.title}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4">
                      {article.description}
                    </p>

                    <div className="flex items-center text-sm text-gray-500">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('education.readTime', { minutes: article.readingTime })}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <div className="mt-16 bg-primary-50 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Ready to understand your situation?
        </h2>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Our free assessment can help you understand which of these categories might apply to your situation.
        </p>
        <Link to="/questionnaire" className="btn-primary">
          Start Free Assessment
        </Link>
      </div>
    </div>
  );
}
